"use client";

import { cn } from "@/lib/utils";
import type { AnnotatedImage } from "@/schemas/analyzed-property";
import { ChevronLeft, ChevronRight, Sparkles, Wand2 } from "lucide-react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { GalleryModal } from "./gallery-modal";
import type { GalleryItem } from "./gallery-modal";

interface QuickVisualGalleryProps {
  images: AnnotatedImage[];
  className?: string;
  onReimagineClick: (imageUrl: string) => void;
}

export function QuickVisualGallery({
  images,
  className,
  onReimagineClick,
}: QuickVisualGalleryProps) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAnnotationsVisible, setIsAnnotationsVisible] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  // Filter out any images without valid URLs
  const validImages = images?.filter((img) => img?.meta?.originalUrl) ?? [];

  // Early return if no valid images
  if (!validImages.length) return null;

  const currentImage = validImages[selectedImageIndex];

  // Check if scrollable and update arrow visibility
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const checkScrollable = () => {
      const isScrollableNow = container.scrollWidth > container.clientWidth;
      setIsScrollable(isScrollableNow);
      setShowRightScroll(isScrollableNow);
      setShowLeftScroll(container.scrollLeft > 0);
      setShowRightScroll(
        container.scrollLeft < container.scrollWidth - container.clientWidth,
      );
    };

    checkScrollable();
    container.addEventListener("scroll", checkScrollable);
    window.addEventListener("resize", checkScrollable);

    // Update selected index based on scroll position
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const scrollLeft = container.scrollLeft;
      const containerWidth = container.clientWidth;
      const newIndex = Math.round(scrollLeft / containerWidth);
      setSelectedImageIndex(newIndex);
    };

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", checkScrollable);
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", checkScrollable);
    };
  }, [validImages]);

  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth;
    const newScrollLeft =
      direction === "left"
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    });

    // Update selected index
    const newIndex = Math.round(newScrollLeft / scrollAmount);
    setSelectedImageIndex(
      Math.max(0, Math.min(newIndex, validImages.length - 1)),
    );
  };

  return (
    <div className={cn("relative space-y-2 sm:space-y-4", className)}>
      {/* Main Image Display */}
      <div className="relative w-full overflow-hidden rounded-lg sm:rounded-xl">
        <div
          className="flex gap-0 overflow-x-auto snap-x snap-mandatory scrollbar-hide [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          ref={scrollContainerRef}
        >
          {validImages.map((image, index) => (
            <div
              key={`${image.meta?.originalUrl}-${index}`}
              className="relative flex-none w-full first:ml-0 snap-center"
              style={{ aspectRatio: "4/3" }}
            >
              <div className="relative h-full w-full overflow-hidden rounded-lg">
                <Image
                  src={image.meta?.originalUrl ?? ""}
                  alt={image.item}
                  fill
                  className="object-cover"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 80vw, 70vw"
                  priority={index === 0}
                />

                {/* Base gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

                {/* Bounding Box */}
                {isAnnotationsVisible && image.meta?.boundingBox && (
                  <div
                    className={cn(
                      "absolute border-2 z-10",
                      image.type === "high_value_feature"
                        ? "border-emerald-400 bg-emerald-400/20"
                        : "border-amber-400 bg-amber-400/20",
                    )}
                    style={{
                      left: `${Math.max(1, image.meta.boundingBox.x / 15.36)}%`,
                      top: `${Math.max(1, image.meta.boundingBox.y / 11.52)}%`,
                      width: `${Math.min(98 - Math.max(1, image.meta.boundingBox.x / 15.36), image.meta.boundingBox.width / 15.36)}%`,
                      height: `${Math.min(98 - Math.max(1, image.meta.boundingBox.y / 11.52), image.meta.boundingBox.height / 11.52)}%`,
                    }}
                  >
                    <div
                      className={cn(
                        "absolute px-2 py-1 text-[10px] sm:text-xs font-semibold rounded whitespace-nowrap shadow-sm",
                        "transition-all duration-200 backdrop-blur-sm",
                        image.type === "high_value_feature"
                          ? "bg-emerald-400 text-emerald-950"
                          : "bg-amber-400 text-amber-950",
                        // Position label inside box if too close to top
                        image.meta.boundingBox.y / 11.52 <= 8
                          ? "top-1 left-1"
                          : "-top-6 left-0",
                      )}
                    >
                      {image.item}
                    </div>
                  </div>
                )}

                {/* Caption gradient overlay - above annotations */}
                <div className="absolute inset-x-0 bottom-0 h-24 sm:h-36 bg-gradient-to-t from-black/95 via-black/50 to-transparent z-20" />

                {/* Image Info - highest z-index */}
                <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4 z-30">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    {image.type === "high_value_feature" ? (
                      <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-400" />
                    ) : (
                      <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-amber-400" />
                    )}
                    <p className="text-xs sm:text-sm font-medium text-white">
                      {image.item}
                    </p>
                  </div>
                  {image.detail && (
                    <p className="mt-1 text-xs sm:text-sm text-white/80 line-clamp-2">
                      {image.detail}
                    </p>
                  )}
                </div>

                {/* Floating Reimagine Button */}
                <Button
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-black/60 hover:bg-black text-white backdrop-blur-sm group z-30 h-7 sm:h-8 px-2.5 sm:px-3 text-xs sm:text-sm"
                  size="sm"
                  onClick={() =>
                    currentImage.meta?.originalUrl &&
                    onReimagineClick(currentImage.meta.originalUrl)
                  }
                >
                  <Wand2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 transition-transform group-hover:rotate-12" />
                  Reimagine
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Scroll Controls */}
        {showLeftScroll && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-2 top-1/2 -translate-y-1/2 h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-black/40 text-white hover:bg-black/60 backdrop-blur-sm z-30"
            onClick={() => scroll("left")}
          >
            <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        )}
        {showRightScroll && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-black/40 text-white hover:bg-black/60 backdrop-blur-sm z-30"
            onClick={() => scroll("right")}
          >
            <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        )}
      </div>

      {/* Progress Dots */}
      <div className="flex justify-center gap-1 sm:gap-1.5">
        {validImages.map((image, index) => (
          <button
            key={`${image.meta?.originalUrl}-${index}`}
            type="button"
            className={cn(
              "w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full transition-all duration-300",
              index === selectedImageIndex
                ? "bg-primary w-2 sm:w-3"
                : "bg-primary/30 hover:bg-primary/50",
            )}
            onClick={() => {
              const container = scrollContainerRef.current;
              if (!container) return;

              const scrollAmount = container.clientWidth;
              container.scrollTo({
                left: index * scrollAmount,
                behavior: "smooth",
              });
              setSelectedImageIndex(index);
            }}
            aria-label={`View image ${index + 1} of ${validImages.length}`}
          />
        ))}
      </div>

      {/* Full Screen Gallery Modal */}
      <GalleryModal
        items={validImages as GalleryItem[]}
        initialIndex={selectedImageIndex}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
      />
    </div>
  );
}
